import {jsPDF} from "jspdf";
import moment from "moment";

import {LIGHT_COLORS as C} from "@/style/utils";

// Base comune dei report PDF: stessa testata, scheda dati, tabelle e piè di pagina.
// I PDF si stampano: sempre colori del tema chiaro.

const PAD = 40;
const FOOT = 40;
const ROW_H = 22;
const BAND_H = 64;

export const pdfText = (value, fallback = "—") =>
	value === null || value === undefined || value === ""
		? fallback
		: String(value);

export const pdfFileName = (...parts) =>
	parts
		.filter(Boolean)
		.map((p) =>
			String(p)
				.replace(/[\\/:*?"<>|]/g, "-")
				.replace(/\s+/g, "_"),
		)
		.join("_") + ".pdf";

export function createReport({title, club, subtitle}) {
	const doc = new jsPDF({unit: "pt", format: "a4"});
	const PW = doc.internal.pageSize.getWidth();
	const PH = doc.internal.pageSize.getHeight();
	const W = PW - PAD * 2;
	let y = PAD;
	let repeatOnNewPage = null;

	const font = (style, size, color = C.text) => {
		doc.setFont("helvetica", style);
		doc.setFontSize(size);
		doc.setTextColor(color);
	};

	// Testo tagliato con "…" se non entra nella larghezza
	const fit = (text, width) => {
		let t = pdfText(text, "");
		if (doc.getTextWidth(t) <= width) return t;
		while (t.length > 1 && doc.getTextWidth(t + "…") > width)
			t = t.slice(0, -1);
		return t + "…";
	};

	const ensure = (height) => {
		if (y + height <= PH - FOOT) return;
		doc.addPage();
		y = PAD;
		repeatOnNewPage && repeatOnNewPage();
	};

	const header = () => {
		doc.setFillColor(C.primaryDark);
		doc.rect(0, 0, PW, BAND_H, "F");
		font("normal", 9, "#a9c1ea");
		doc.text(fit(club, W / 2), PAD, 26);
		font("bold", 17, "#ffffff");
		doc.text(fit(title, W / 2), PAD, 47);
		if (subtitle) {
			font("normal", 9.5, "#d6e2f5");
			const lines = [].concat(subtitle).filter(Boolean).slice(0, 2);
			lines.forEach((line, i) =>
				doc.text(fit(line, W / 2 - 8), PW - PAD, 30 + i * 15, {
					align: "right",
				}),
			);
		}
		y = BAND_H + 18;
	};

	// Coppie etichetta/valore, quattro per riga
	const infoCard = (items) => {
		const shown = items.filter((it) => it.value);
		if (!shown.length) return;
		const perRow = 4;
		const cellW = W / perRow;
		const rows = Math.ceil(shown.length / perRow);
		const cardH = rows * 34 + 8;
		ensure(cardH + 12);
		doc.setFillColor(C.surfaceSoft);
		doc.setDrawColor(C.border);
		doc.setLineWidth(0.8);
		doc.roundedRect(PAD, y, W, cardH, 6, 6, "FD");
		shown.forEach((it, i) => {
			const cx = PAD + (i % perRow) * cellW + 10;
			const cy = y + Math.floor(i / perRow) * 34 + 18;
			font("normal", 7.5, C.textSecondary);
			doc.text(fit(it.label, cellW - 16), cx, cy);
			font("bold", 10);
			doc.text(fit(it.value, cellW - 16), cx, cy + 14);
		});
		y += cardH + 16;
	};

	const section = (label) => {
		ensure(ROW_H * 3);
		font("bold", 11, C.primaryDark);
		doc.text(label, PAD, y + 10);
		y += 18;
	};

	// columns: [{label, width (frazione della larghezza), align, bold}]
	const table = (columns, rows, emptyText = "Nessuno") => {
		const total = columns.reduce((n, c) => n + (c.width || 1), 0);
		const cols = columns.reduce((list, c) => {
			const x = list.length
				? list[list.length - 1].x + list[list.length - 1].w
				: PAD;
			return [...list, {...c, x, w: ((c.width || 1) / total) * W}];
		}, []);
		const textX = (c) =>
			c.align === "right"
				? c.x + c.w - 6
				: c.align === "center"
					? c.x + c.w / 2
					: c.x + 6;
		const drawHead = () => {
			doc.setFillColor(C.bg);
			doc.rect(PAD, y, W, ROW_H - 2, "F");
			font("bold", 8, C.textSecondary);
			cols.forEach((c) =>
				doc.text(fit(c.label, c.w - 10), textX(c), y + 13, {
					align: c.align || "left",
				}),
			);
			y += ROW_H - 2;
		};
		ensure(ROW_H * 2);
		drawHead();
		repeatOnNewPage = drawHead;
		if (!rows.length) {
			font("normal", 9.5, C.textSecondary);
			doc.text(emptyText, PAD + 6, y + 14);
			y += ROW_H;
		}
		rows.forEach((row, i) => {
			ensure(ROW_H);
			if (i % 2) {
				doc.setFillColor(C.surfaceSoft);
				doc.rect(PAD, y, W, ROW_H, "F");
			}
			doc.setDrawColor(C.border);
			doc.setLineWidth(0.4);
			doc.line(PAD, y + ROW_H, PAD + W, y + ROW_H);
			cols.forEach((c, k) => {
				font(c.bold ? "bold" : "normal", 9.5);
				doc.text(fit(row[k], c.w - 10), textX(c), y + 14.5, {
					align: c.align || "left",
				});
			});
			y += ROW_H;
		});
		repeatOnNewPage = null;
		y += 14;
	};

	// Informazioni in evidenza a tutta larghezza (ritrovo, divisa)
	const facts = (items) => {
		const shown = items.filter((it) => it.value);
		if (!shown.length) return;
		const labelW = 110;
		shown.forEach((it) => {
			font("bold", 12);
			const lines = doc.splitTextToSize(String(it.value), W - labelW);
			ensure(lines.length * 16 + 6);
			font("normal", 9, C.textSecondary);
			doc.text(it.label, PAD, y + 12);
			font("bold", 12);
			lines.forEach((line, i) => doc.text(line, PAD + labelW, y + 12 + i * 16));
			y += lines.length * 16 + 8;
		});
		y += 10;
	};

	const paragraph = (label, text) => {
		if (!text) return;
		font("normal", 10);
		const lines = doc.splitTextToSize(String(text), W);
		ensure(30);
		font("bold", 8, C.textSecondary);
		doc.text(label, PAD, y + 8);
		y += 22;
		font("normal", 10);
		lines.forEach((line) => {
			ensure(14);
			doc.text(line, PAD, y);
			y += 14;
		});
		y += 10;
	};

	// Spazi per le firme in fondo
	const signatures = (labels) => {
		ensure(60);
		const w = W / labels.length;
		labels.forEach((label, i) => {
			const x = PAD + i * w;
			doc.setDrawColor(C.textSecondary);
			doc.setLineWidth(0.6);
			doc.line(x + 8, y + 36, x + w - 16, y + 36);
			font("normal", 8, C.textSecondary);
			doc.text(label, x + 8, y + 48);
		});
		y += 60;
	};

	const finish = (fileName) => {
		const pages = doc.internal.getNumberOfPages();
		const stamp = "Generato il " + moment().format("DD/MM/YYYY · HH:mm");
		for (let i = 1; i <= pages; i++) {
			doc.setPage(i);
			doc.setDrawColor(C.border);
			doc.setLineWidth(0.8);
			doc.line(PAD, PH - FOOT + 12, PW - PAD, PH - FOOT + 12);
			font("normal", 8, C.textSecondary);
			doc.text(stamp, PAD, PH - 16);
			doc.text(`Pagina ${i} di ${pages}`, PW - PAD, PH - 16, {align: "right"});
		}
		doc.save(fileName);
	};

	return {
		doc,
		header,
		infoCard,
		facts,
		section,
		table,
		paragraph,
		signatures,
		finish,
	};
}
